import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Redirect } from 'expo-router';
import { PickerAspectOption } from 'features/menu/components/product-detail/picker-aspect-selector/picker-aspect-option';
import {
  PickerAspectOptionData,
  getPickerAspects,
} from 'features/menu/components/product-detail/picker-aspect-selector/utils';
import { PickerAspectsUpdateDialog } from 'features/menu/components/product-detail/product-detail-flow/dialogs/picker-aspects-update-dialog';
import { SelectorContainer } from 'features/menu/components/product-detail/styled';
import { useProductWizardContext } from 'features/menu/components/product-wizard';

import { ListCardGroup } from 'components/list-card-group';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

interface PickerAspectSelectorProps {
  pickerAspectId: string;
  isFirstAspect: boolean;
}

export const PickerAspectSelector = ({
  pickerAspectId,
  isFirstAspect,
}: PickerAspectSelectorProps) => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const {
    pickerSelections,
    selectPickerAspectOption,
    isAnySlotCustomized,
  } = useProductWizardContext();
  const [showDialog, setShowDialog] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);

  const { pickerAspect, pickerAspectOptions } = useMemo(() => {
    try {
      return getPickerAspects(indexedMenu, posServiceMode, pickerAspectId);
    } catch (error) {
      logger.warn({
        message: '[PickerAspectSelector] Failed to load picker aspect data, redirecting to menu',
        context: { pickerAspectId, isFirstAspect, posServiceMode },
        error,
      });
      return { pickerAspect: null, pickerAspectOptions: [] };
    }
  }, [indexedMenu, posServiceMode, pickerAspectId, isFirstAspect]);

  const onConfirm = useCallback(() => {
    pendingAction.current?.();
    setShowDialog(false);
  }, []);

  const onCancel = useCallback(() => {
    pendingAction.current = null;
    setShowDialog(false);
  }, []);

  const onSelected = useCallback(
    (pickerAspectId: string, aspectOptionId: string) => {
      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.PRODUCT_SELECTION_MODIFICATION,
          componentId: pickerAspectId.split('$').pop(),
          headerText: pickerAspect?.name?.locale ?? '',
          name: aspectOptionId.split('$').pop() ?? '',
        },
      });

      if (isAnySlotCustomized()) {
        // save the action to be executed after the dialog is closed
        pendingAction.current = () => selectPickerAspectOption(pickerAspectId, aspectOptionId);
        setShowDialog(true);
        return;
      }
      selectPickerAspectOption(pickerAspectId, aspectOptionId);
    },
    [isAnySlotCustomized, pickerAspect?.name?.locale, selectPickerAspectOption]
  );

  if (!pickerAspect || !pickerAspectOptions.length) {
    return <Redirect href={routes.menu} />;
  }

  return (
    <SelectorContainer testID="product-picker-flow">
      <ListCardGroup
        header={pickerAspect.name?.locale ?? ''}
        items={pickerAspectOptions}
        renderItem={({
          item: aspectOptionData,
          index,
        }: {
          item: PickerAspectOptionData;
          index: number;
        }) => (
          <PickerAspectOption
            key={index}
            aspectOption={aspectOptionData.aspectOption}
            product={aspectOptionData.defaultProduct}
            selected={aspectOptionData.aspectOption.id === pickerSelections[pickerAspect.id]}
            onSelected={() => onSelected(pickerAspectId, aspectOptionData.aspectOption.id)}
            showPriceUpcharge={!isFirstAspect}
            upcharge={aspectOptionData.upcharge}
          />
        )}
      />
      <PickerAspectsUpdateDialog
        showDialog={showDialog}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </SelectorContainer>
  );
};
